import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteDialog from "../../../components/DeleteDialog";
import { useLocalStorage } from "../../../hooks";
import * as dashboardActions from "../../../redux/reducers/dashboardReducer";
import { userState } from "../../../redux/reducers/userReducer";

import { PlusIcon } from "@heroicons/react/20/solid";
import delete_svg from "../../../assets/delete_svg.svg";
import edit_svg from "../../../assets/edit_svg.svg";
import ticketStarIcon from "../../../assets/ticketStarIcon.png";
import {
  TwButton,
  TwCheckBox,
  TwDropzone,
  TwFormHelperText,
  TwInputGroup,
  TwSelectGroup,
  TwTextarea,
  TwTooltip,
} from "../../../components/tailwind";
import TwDrawer from "../../../components/tailwind/TwDrawer";
import { useToast } from "../../../provider/ToastProvider";
import { loadingState } from "../../../redux/reducers/loadingReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { listProducts } from "../../../utils/productsApi";
import { defaultAvailablePaymentMethods } from "../../../utils/constants";
import PriceHelperText from "../../../components/PriceHelperText";
import { useTranslation } from "react-i18next";
import { decimalDisplayValue, decimalOnChange } from "../../../helpers";

export default function AddonsTab({
  profileFor,
  formValues,
  setFormValues,
  setTabCurrentState,
  edit = false,
  handleUpdate,
  isFromDetail = false,
}) {
  const { t, i18n } = useTranslation();
  const { addConfirmDialog } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loadingReduxState = useSelector(loadingState);

  const handleFormValueChange = (event) => {
    const { name, value } = event.target;
    setFormPriceValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const userReduxState = useSelector(userState);
  const [isEdit, setIsEdit] = React.useState(-1);
  const [isFirstLoad, setIsFirstLoad] = React.useState(false);
  const [formPriceErrors, setFormPriceErrors] = React.useState({});
  const [formPriceValues, setFormPriceValues] = React.useState({
    id: "",
    addonType: "",
    addonphoto: "",
    addonName: "",
    capacity: 100,
    maxQtyPerTicket: 1,
    amountForTicket: 0,
    amountForLocation: 0,
    description: "",
    absorbServiceFee: false,
    availableForSale: true,
    // Reference back to the catalogue product this add-on was created from, if any.
    productId: "",
  });
  const [addonsPricingList, setAddonsPricingList] = React.useState([]);
  const [addAddons, setAddAddons] = React.useState(false);
  const [delDialogOpen, setDelDialogOpen] = React.useState(false);
  const [userToken] = useLocalStorage("userToken");
  const [availablePaymentMethods, setAvailablePaymentMethods] = React.useState(
    defaultAvailablePaymentMethods,
  );

  const [imageProgress, setImageProgress] = React.useState(0);
  const [addonTypes] = React.useState([
    "drinks",
    "food",
    "special_access",
    "merchandise",
  ]);
  const [isAllowForPaidTicket, setIsAllowForPaidTicket] = React.useState(true);
  const [addDesc, setAddDesc] = React.useState(false);
  const [addPhoto, setAddPhoto] = React.useState(false);

  // Reusable product catalogue, so an organizer picks an existing item instead of retyping
  // its name, description and photo for every event.
  const [catalogueProducts, setCatalogueProducts] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      const result = await listProducts();
      if (!result.error && Array.isArray(result.data)) {
        setCatalogueProducts(result.data);
      }
    })();
  }, []);

  /**
   * Copy the catalogue product's display fields onto the add-on and keep a productId
   * reference.
   *
   * Copy rather than reference on purpose: a finished event must not change because someone
   * later edited the catalogue. The productId preserves traceability without that coupling.
   *
   * The catalogue's onSitePrice populates "Price at Venue" (amountForLocation) — they are the
   * same concept: what someone pays for this item at the venue. The ONLINE price
   * (amountForTicket) is left alone, because that is the organizer's decision per event.
   */
  const applyCatalogueProduct = (productObjectId) => {
    if (!productObjectId) {
      setFormPriceValues((prevValues) => ({ ...prevValues, productId: "" }));
      return;
    }
    const product = catalogueProducts.find((p) => p._id === productObjectId);
    if (!product) return;

    if (product.description) setAddDesc(true);
    if (product.photo) setAddPhoto(true);

    setFormPriceValues((prevValues) => ({
      ...prevValues,
      productId: product._id,
      addonName: product.name || prevValues.addonName,
      description: product.description || prevValues.description,
      addonphoto: product.photo || prevValues.addonphoto,
      amountForLocation:
        product.onSitePrice !== undefined && product.onSitePrice !== null
          ? parseFloat(product.onSitePrice)
          : prevValues.amountForLocation,
    }));
  };

  const getAvailablePaymentMethods = async () => {
    const requestConfig = {
      method: "get",
      url: `${getUrlForBookings()}payments/getAvailablePaymentMethods?organizerId=${userReduxState.userData?._id}&paymentMethodsFor=event&bookingFor=event`,
    };
    const response = await axiosInstance(requestConfig);
    if (response.status === 200) {
      if (response.data.statusCode === 403) {
      } else if (response.data.statusCode === 200) {
        if (response.data.data) {
          setAvailablePaymentMethods({
            ...{
              payAtVenue: {
                enable: false,
                processingFeeApplicable: true,
                reserveNowPaylater: true,
                paymentGatewayServiceFee: {
                  paymentProcessingFlatFee: 0,
                  paymentProcessingPercentage: 0,
                },
              },
            },
            ...response.data.data,
          });
        }
      }
    }
  };

  const calculatbuyerCost = (amount, onlyServicefee = true) => {
    let defaultPercentage = 0;
    if (availablePaymentMethods?.bookzyPlatformFee) {
      let flatFee = 0;
      let percentageFee = 0;
      if (availablePaymentMethods?.bookzyPlatformFee?.flatFee > 0) {
        flatFee = availablePaymentMethods?.bookzyPlatformFee?.flatFee;
      }
      if (availablePaymentMethods?.bookzyPlatformFee?.percentageFee > 0) {
        percentageFee =
          availablePaymentMethods?.bookzyPlatformFee?.percentageFee;
      }
      return parseFloat(amount) > 0
        ? parseFloat(
            (onlyServicefee ? parseFloat(amount) : 0) +
              parseFloat(flatFee) +
              parseFloat(amount) * parseFloat(percentageFee / 100),
          ).toFixed(2)
        : 0;
    }
    return parseFloat(amount) > 0
      ? parseFloat(
          (onlyServicefee ? parseFloat(amount) : 0) +
            parseFloat(amount) * parseFloat(defaultPercentage / 100),
        ).toFixed(2)
      : 0;
  };

  const handleDialogOpen = () => {
    setDelDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDelDialogOpen(false);
  };

  const handleDelete = async (price) => {
    let pricesCopy = [...addonsPricingList];
    if (Array.isArray(pricesCopy)) {
      pricesCopy = pricesCopy.filter((item) => item !== price);
    }
    setFormValues((prevValues) => ({
      ...prevValues,
      addonsPricingList: [...pricesCopy],
    }));
    setAddonsPricingList((prevValues) => pricesCopy);
    setIsEdit(-1);
    handleDialogClose();
  };

  const clearForm = () => {
    setFormPriceValues({
      id: "",
      addonType: "",
      addonphoto: "",
      addonName: "",
      capacity: 100,
      maxQtyPerTicket: 1,
      amountForTicket: 0,
      amountForLocation: 0,
      description: "",
      absorbServiceFee: false,
      availableForSale: true,
      productId: "",
    });
    setAddDesc(false);
    setAddPhoto(false);
  };

  const handleSubmit = async () => {
    const errors = {};
    // if (formValues.addonsPricingList.length <= 0) {
    //   errors.addonsPricingList = "Please add at least one addons!";
    // }
    setFormPriceErrors({ ...errors });
    if (Object.keys(errors).length > 0) {
      return;
    }
    setTabCurrentState("4");
  };

  React.useEffect(() => {
    if (edit && formValues?.editSubmit && formValues.editSubmit === true) {
      handleUpdate();
      setFormValues((prevValues) => ({
        ...prevValues,
        editSubmit: false,
      }));
    }
    setIsFirstLoad(true);
    setAddonsPricingList(formValues.addonsPricingList);
  }, [formValues.addonsPricingList]);

  React.useEffect(() => {
    if (formPriceValues.description != "") {
      setAddDesc(true);
    }
    let pricesCopy = [...formValues.addonsPricingList];
    if (!formValues.addonsPricingList.length) {
      // pricesCopy.push(createData("Free Addmission", "0", "10", false))
      setFormValues((prevValues) => ({
        ...prevValues,
        addonsPricingList: pricesCopy,
      }));
    }
    setIsEdit(-1);
    setAddonsPricingList(pricesCopy);
    getAvailablePaymentMethods();
  }, []);

  React.useEffect(() => {
    if (formPriceValues.description != "") {
      setAddDesc(true);
    }
    if (formPriceValues.addonphoto != "") {
      setAddPhoto(true);
    }
  }, [formPriceValues]);

  React.useEffect(() => {
    setIsAllowForPaidTicket(true);
    if (
      userReduxState.userData?.myConnectedPaymentProfiles?.stripe &&
      userReduxState.userData?.myConnectedPaymentProfiles?.stripe
        ?.profileDetails?.stripeAcId &&
      userReduxState.userData?.myConnectedPaymentProfiles?.stripe
        ?.profileDetails?.stripeAcId !== "" &&
      parseFloat(formPriceValues.amountForTicket) > 0
    ) {
      setIsAllowForPaidTicket(false);
    }
  }, [isEdit]);

  return (
    <div
      className={`flex flex-col gap-4  ${edit === false ? "py-6" : ""} ${isFromDetail === false ? "sm:px-8 [sm:w-4/5]" : ""}`}
    >
      <div
        className={`flex flex-col gap-4 ${edit === false ? "p-6 border-2 border-bright-gray rounded-2xl mt-5" : ""}`}
      >
        <div className={"flex gap-2 items-center w-full"}>
          <div
            className={`items-left w-full ${edit === false ? "my-5" : ""} ${isFromDetail === false ? "text-base sm:text-2xl" : "text-2xl sm:text-3xl"}`}
          >
            {t("myEvents.eventAddons")}
          </div>
          <div
            className={`flex justify-end w-full ${edit === false ? "mt-5" : ""}`}
          >
            <TwButton
              variant={"default-box"}
              className="truncate"
              onClick={() => {
                clearForm();
                setAddAddons(true);
              }}
            >
              <PlusIcon className="size-5" />
              &nbsp;&nbsp;{t("myEvents.addNewAddon")}
            </TwButton>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle sm:px-1 overflow-x-auto">
            <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-form">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 truncate"
                    >
                      {t("myEvents.addonName")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 truncate"
                    >
                      {t("myEvents.addonType")}
                    </th>
                    {/* <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 truncate">
                      {t("myEvents.qtyPerTicket")}
                    </th> */}
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 truncate"
                    >
                      {t("myEvents.capacity")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 truncate"
                    >
                      {t("myEvents.priceOnline")}
                    </th>
                    {/* <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 truncate">
                      {t("myEvents.priceAtVenue")}
                    </th> */}
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">
                        {t("account.tableActionHead")}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {addonsPricingList.length > 0 ? (
                    addonsPricingList.map((row, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-gray-900 sm:pl-6 flex gap-2 items-center">
                          {row.addonName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">
                          {row.addonType
                            .replace("_", " ")
                            .toLowerCase()
                            .replace(/\b[a-z]/g, function (letter) {
                              return letter.toUpperCase();
                            })}
                        </td>
                        {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">{row.maxQtyPerTicket}</td> */}
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">
                          {row.capacity}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">
                          <div>
                            €{row.amountForTicket}
                            <p className="text-sm text-gray-400">
                              {t("myEvents.costToBuyer")} €
                              {row.absorbServiceFee === true
                                ? parseFloat(row.amountForTicket)
                                : calculatbuyerCost(row.amountForTicket)}
                            </p>
                          </div>
                        </td>
                        {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">€{row.amountForLocation}</td> */}
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex gap-2 justify-center items-center">
                            <button
                              onClick={() => {
                                setIsEdit(i);
                                setFormPriceValues({
                                  capacity: 100,
                                  absorbServiceFee: false,
                                  availableForSale: true,
                                  ...row,
                                });
                                setAddAddons(true);
                              }}
                              className="size-7"
                            >
                              <TwTooltip title={t("inputFieldsTitle.edit")}>
                                <img
                                  src={edit_svg}
                                  alt={t("inputFieldsTitle.edit")}
                                  className="size-7"
                                />
                              </TwTooltip>
                            </button>
                            <button
                              onClick={() => {
                                setIsEdit(i);
                                handleDialogOpen();
                              }}
                              className="size-7"
                            >
                              <TwTooltip title={t("inputFieldsTitle.delete")}>
                                <img
                                  src={delete_svg}
                                  alt={t("inputFieldsTitle.delete")}
                                  className="size-7"
                                />
                              </TwTooltip>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 text-center"
                      >
                        {t("myEvents.noAddonsCreated")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {formPriceErrors["addonsPricingList"] && (
          <TwFormHelperText error={true}>
            {formPriceErrors["addonsPricingList"]}
          </TwFormHelperText>
        )}
      </div>
      {isFromDetail === false ? (
        <div className={"flex gap-3 justify-center items-center pt-12 sm:px-8"}>
          <TwButton
            variant={"secondary-outline"}
            className={"w-full sm:w-60"}
            disabled={loadingReduxState.actionLoading}
            onClick={() => {
              if (edit) {
                dispatch(dashboardActions.setCalendarSessionId(-1));
                dispatch(dashboardActions.setCalendarOpen(false));
                navigate("/home/dashboard");
              } else {
                setTabCurrentState("2");
              }
            }}
          >
            {edit ? t("inputFieldsTitle.cancel") : t("inputFieldsTitle.back")}
          </TwButton>
          <TwButton
            variant={"secondary"}
            className={"w-full sm:w-60"}
            disabled={loadingReduxState.actionLoading}
            actionLoading={loadingReduxState.actionLoading}
            onClick={() => {
              if (edit) {
                handleUpdate();
              } else {
                handleSubmit();
              }
            }}
          >
            {edit ? t("update") : t("inputFieldsTitle.continue")}
          </TwButton>
        </div>
      ) : (
        ""
      )}
      <DeleteDialog
        open={delDialogOpen}
        title={t("myEvents.removeAddonTitle")}
        type={"addon"}
        handleClose={handleDialogClose}
        handleDelete={() => handleDelete(addonsPricingList[isEdit])}
      />
      <TwDrawer
        title={`${isEdit >= 0 ? t("update") : "Add"} Addon`}
        classNameDialogPanel={`!max-w-md`}
        open={addAddons}
        onClose={() => {
          setIsEdit(-1);
          clearForm();
          setAddAddons(false);
        }}
        footerChildren={
          <>
            <TwButton
              variant={"secondary-outline"}
              className={"w-1/3"}
              onClick={() => {
                setIsEdit(-1);
                setTimeout(() => {
                  clearForm();
                }, 1000);
                setAddAddons(false);
              }}
            >
              {t("inputFieldsTitle.cancel")}
            </TwButton>
            <TwButton
              variant={"secondary"}
              className={"w-1/3 truncate"}
              disabled={loadingReduxState.actionLoading}
              actionLoading={loadingReduxState.actionLoading}
              onClick={() => {
                const errors = {};
                if (!formPriceValues.addonType) {
                  errors.addonType = t("errors.required");
                } else {
                  addonsPricingList.map((row, i) => {
                    if (
                      isEdit !== i &&
                      formPriceValues.addonType.value === row.addonType
                    ) {
                      errors.addonType = t("myEvents.sameAddonType");
                    }
                    return row;
                  });
                }
                /* if (!formPriceValues.addonphoto) {
                  errors.addonphoto = t("errors.required");
                } */
                if (!formPriceValues.addonName) {
                  errors.addonName = t("errors.required");
                }
                if (
                  formPriceValues.capacity === "" ||
                  formPriceValues.capacity <= 0
                ) {
                  errors.capacity = t("errors.required");
                }
                if (formPriceValues.capacity % 1 !== 0) {
                  errors.capacity = t("errors.decimalValueOnly");
                }

                if (isEdit >= 0) {
                  let adonDetail = formValues.addonsPricingList.find(
                    (d, i) => isEdit === i,
                  );
                  if (
                    adonDetail &&
                    adonDetail?.capacity != undefined &&
                    adonDetail?.addonsBooked &&
                    adonDetail.addonsBooked.addonsBookedConfirmed !=
                      undefined &&
                    parseInt(formPriceValues.capacity) <
                      parseInt(adonDetail.addonsBooked.addonsBookedConfirmed)
                  ) {
                    errors.capacity = t("myEvents.addonCapacityMustBeBooking");
                  }
                }

                if (
                  formPriceValues.maxQtyPerTicket === "" ||
                  formPriceValues.maxQtyPerTicket <= 0
                ) {
                  errors.maxQtyPerTicket = t("errors.required");
                }
                if (formPriceValues.maxQtyPerTicket % 1 !== 0) {
                  errors.maxQtyPerTicket = t("errors.decimalValueOnly");
                }
                if (
                  formPriceValues.amountForTicket === "" ||
                  formPriceValues.amountForTicket < 0
                ) {
                  errors.amountForTicket = t("errors.required");
                }

                if (
                  formPriceValues.absorbServiceFee === true &&
                  parseFloat(formPriceValues.amountForTicket) > 0
                ) {
                  const serviceFee = parseFloat(
                    calculatbuyerCost(formPriceValues.amountForTicket, false),
                  );
                  if (
                    parseFloat(formPriceValues.amountForTicket) < serviceFee
                  ) {
                    errors.amountForTicket = t(
                      "errors.priceLessThanServiceFee",
                      { serviceFee: `€${serviceFee}` },
                    );
                  }
                }

                if (
                  formPriceValues.amountForLocation === "" ||
                  formPriceValues.amountForLocation < 0
                ) {
                  errors.amountForLocation = t("errors.required");
                }
                if (
                  parseFloat(formPriceValues.amountForLocation) > 0 &&
                  parseFloat(formPriceValues.amountForTicket) > 0 &&
                  parseFloat(formPriceValues.amountForLocation) <
                    parseFloat(formPriceValues.amountForTicket)
                ) {
                  errors.amountForLocation = t(
                    "errors.venueCannotBeLessThanOnline",
                  );
                }
                // if (!formPriceValues.description) {
                //   errors.description = t("errors.required");
                // }
                setFormPriceErrors({ ...errors });
                if (Object.keys(errors).length > 0) {
                  return;
                }
                let pricesCopy = [];
                if (isEdit >= 0) {
                  pricesCopy = addonsPricingList.map((row, i) => {
                    if (isEdit === i) {
                      return formPriceValues;
                    }
                    return row;
                  });
                } else {
                  pricesCopy = [...addonsPricingList, formPriceValues];
                }

                let finalPrices = pricesCopy.map((d) => {
                  return {
                    ...d,
                    capacity: d.capacity !== undefined ? d.capacity : 100,
                    absorbServiceFee:
                      d.absorbServiceFee !== undefined
                        ? d.absorbServiceFee
                        : false,
                    availableForSale:
                      d.availableForSale !== undefined
                        ? d.availableForSale
                        : true,
                  };
                });
                setIsEdit(-1);
                setAddonsPricingList(finalPrices);
                setFormValues((prevValues) => ({
                  ...prevValues,
                  addonsPricingList: finalPrices,
                  ...edit ? { editSubmit: true } : {},
                }));
                setTimeout(() => {
                  clearForm();
                }, 1000);
                setAddAddons(false);
              }}
            >
              {isEdit >= 0 ? t("update") : t("myEvents.addAddon")}
            </TwButton>
          </>
        }
      >
        <div className={"flex flex-col gap-3"}>
          <TwSelectGroup
            label={t("myEvents.addonType")}
            placeholder={t("myEvents.selectAddonType")}
            name={"addonType"}
            leftIcon={<img className="size-5" src={ticketStarIcon} />}
            options={[
              { value: "", title: t("myEvents.selectAddonType") },
              ...(addonTypes.length
                ? addonTypes.map((addonType, idx) => {
                    return {
                      value: addonType,
                      title: addonType
                        .replace("_", " ")
                        .toLowerCase()
                        .replace(/\b[a-z]/g, function (letter) {
                          return letter.toUpperCase();
                        }),
                    };
                  })
                : []),
            ]}
            defaultValue={formPriceValues.addonType}
            value={formPriceValues.addonType}
            onChange={(event) => {
              setFormPriceValues((prevValues) => ({
                ...prevValues,
                addonType: event.target.value,
              }));
            }}
            errors={formPriceErrors}
          />

          {catalogueProducts.length > 0 && (
            <TwSelectGroup
              label={t("myEvents.useCatalogueProduct", "Use a product from your catalogue")}
              name={"catalogueProduct"}
              value={formPriceValues.productId || ""}
              helperText={t(
                "myEvents.useCatalogueProductHelper",
                "Fills in the name, photo, description and price at venue. You set the online price and capacity below."
              )}
              options={[
                { value: "", title: t("myEvents.enterManually", "Enter manually") },
                ...catalogueProducts.map((p) => ({ value: p._id, title: p.name })),
              ]}
              onChange={(event) => applyCatalogueProduct(event.target.value)}
              errors={formPriceErrors}
            />
          )}

          <TwInputGroup
            label={t("myEvents.addonName")}
            name={"addonName"}
            value={formPriceValues.addonName}
            onChange={handleFormValueChange}
            errors={formPriceErrors}
          />
          {addDesc === false ? (
            <button
              type="button"
              className="rounded inline-flex items-center w-max bg-white px-2 py-1 text-xs font-semibold text-gray-900 ring-0 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => {
                setAddDesc(true);
              }}
            >
              <PlusIcon className="size-5" />
              &nbsp;&nbsp;{t("myEvents.addDescription")}
            </button>
          ) : (
            <TwTextarea
              label={t("myEvents.description")}
              name={"description"}
              value={formPriceValues.description}
              row={3}
              onChange={handleFormValueChange}
              errors={formPriceErrors}
            />
          )}
          {addPhoto ? (
            <TwDropzone
              // variant={"profile"}
              uploadBtnText={
                formPriceValues.addonphoto !== ""
                  ? t("myEvents.changeImage")
                  : t("myEvents.addImage")
              }
              label={t("myEvents.addonPhoto")}
              name={"addonphoto"}
              errors={formPriceErrors}
              value={formPriceValues.addonphoto || ""}
              onChange={(image) => {
                setFormPriceValues((prevValues) => ({
                  ...prevValues,
                  addonphoto: image,
                }));
              }}
            />
          ) : (
            <button
              type="button"
              className="rounded inline-flex items-center w-max bg-white px-2 py-1 text-xs font-semibold text-gray-900 ring-0 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => {
                setAddPhoto(true);
              }}
            >
              <PlusIcon className="size-5" />
              &nbsp;&nbsp;{t("myEvents.addPhoto")}
            </button>
          )}

          <TwInputGroup
            label={t("myEvents.capacity")}
            placeholder={t("myEvents.capacity")}
            type={"number"}
            name={"capacity"}
            leftIcon={<img className="size-5" src={ticketStarIcon} />}
            value={formPriceValues.capacity}
            onChange={handleFormValueChange}
            errors={formPriceErrors}
            className="!py-3 md:pl-2.5"
            helperText={t("myEvents.capacityHelperText")}
          />
          <TwInputGroup
            label={t("myEvents.maxQtyPerTicket")}
            name={"maxQtyPerTicket"}
            type="number"
            leftIcon={<img className="size-5" src={ticketStarIcon} />}
            value={formPriceValues.maxQtyPerTicket}
            onChange={handleFormValueChange}
            errors={formPriceErrors}
          />
          <div className="flex gap-2 items-center">
            <TwInputGroup
              label={t("myEvents.priceOnline")}
              name={"amountForTicket"}
              type="text"
              inputMode="decimal"
              leftIcon={"€"}
              disabled={isAllowForPaidTicket}
              value={decimalDisplayValue(
                formPriceValues.amountForTicket,
                i18n.language,
              )}
              onChange={(event) => {
                const parsed = decimalOnChange(
                  event.target.value,
                  i18n.language,
                );
                if (parsed !== null) {
                  setFormPriceValues((prevValues) => ({
                    ...prevValues,
                    amountForTicket: parsed,
                  }));
                }
              }}
              errors={formPriceErrors}
              helperText={
                <PriceHelperText
                  t={t}
                  amount={formPriceValues.amountForTicket}
                  serviceFee={calculatbuyerCost(formPriceValues.amountForTicket, false)}
                  absorbServiceFee={formPriceValues.absorbServiceFee === true}
                />
              }
            />
            <TwCheckBox
              title={t("myEvents.free")}
              name={"isfree"}
              checked={isAllowForPaidTicket}
              // disabled={isAllowForPaidTicket}
              onChange={(e) => {
                const { name, value, checked } = e.target;
                if (
                  userReduxState.userData?.myConnectedPaymentProfiles?.stripe &&
                  userReduxState.userData?.myConnectedPaymentProfiles?.stripe
                    ?.profileDetails?.stripeAcId &&
                  userReduxState.userData?.myConnectedPaymentProfiles?.stripe
                    ?.profileDetails?.stripeAcId !== "" &&
                  userReduxState.userData?.myConnectedPaymentProfiles?.stripe
                    ?.profileDetails?.stripeOnBoardindStatus &&
                  userReduxState.userData?.myConnectedPaymentProfiles?.stripe
                    ?.profileDetails?.stripeOnBoardindStatus === "SUCCESS"
                ) {
                  setIsAllowForPaidTicket(checked);
                  if (checked === true) {
                    setFormPriceValues((prevValues) => ({
                      ...prevValues,
                      amountForTicket: 0,
                    }));
                  }
                } else {
                  addConfirmDialog({
                    dialogOpen: true,
                    dialogType: "warning",
                    dialogTitle: t("myEvents.paymentNotEnabledTitle"),
                    dialogMessage: t("myEvents.paymentNotEnabledSubTitle"),
                    isCloseBtn: false,
                    btnCloseText: t("inputFieldsTitle.close"),
                    btnConfirmVariant: "secondary",
                    btnConfirmText: t("myEvents.paymentNotEnabledBtn"),
                    onClose: async () => {},
                    onConfirm: async () => {
                      window.open(`/my-account/online-payment`, "_blank");
                      // navigate("/my-account/online-payment", { replace: true });
                    },
                  });
                }
              }}
              errors={formPriceErrors}
            />
          </div>
          <TwCheckBox
            label={t("myEvents.absorbServiceFee")}
            title={t("myEvents.absorbServiceFee")}
            name={"absorbServiceFee"}
            checked={
              formPriceValues.absorbServiceFee !== undefined
                ? formPriceValues.absorbServiceFee
                : false
            }
            onChange={(e) => {
              const { name, value, checked } = e.target;

              setFormPriceValues((prevValues) => ({
                ...prevValues,
                absorbServiceFee: checked,
              }));

              if (
                checked === true &&
                parseFloat(formPriceValues.amountForTicket) > 0
              ) {
                const serviceFee = parseFloat(
                  calculatbuyerCost(formPriceValues.amountForTicket, false),
                );
                if (
                  parseFloat(formPriceValues.amountForTicket) < serviceFee
                ) {
                  setFormPriceErrors((prev) => ({
                    ...prev,
                    amountForTicket: t("errors.priceLessThanServiceFee", {
                      serviceFee: `€${serviceFee}`,
                    }),
                  }));
                } else {
                  setFormPriceErrors((prev) => {
                    const { amountForTicket, ...rest } = prev;
                    return rest;
                  });
                }
              } else {
                setFormPriceErrors((prev) => {
                  const { amountForTicket, ...rest } = prev;
                  return rest;
                });
              }
            }}
            errors={formPriceErrors}
          />
          <TwInputGroup
            label={t("myEvents.priceAtVenue")}
            name={"amountForLocation"}
            type="text"
            inputMode="decimal"
            leftIcon={"€"}
            rightIcon={<span className="text-gray-500 text-xs">EUR</span>}
            value={decimalDisplayValue(
              formPriceValues.amountForLocation,
              i18n.language,
            )}
            onChange={(event) => {
              const parsed = decimalOnChange(event.target.value, i18n.language);
              if (parsed !== null) {
                setFormPriceValues((prevValues) => ({
                  ...prevValues,
                  amountForLocation: parsed,
                }));
              }
            }}
            errors={formPriceErrors}
            helperText={t("myEvents.priceAtVenueHelperText")}
          />
          <TwCheckBox
            label={t("myEvents.availableForSale")}
            title={t("myEvents.availableForSale")}
            name={"availableForSale"}
            checked={
              formPriceValues.availableForSale !== undefined
                ? formPriceValues.availableForSale
                : true
            }
            onChange={(e) => {
              const { name, value, checked } = e.target;

              setFormPriceValues((prevValues) => ({
                ...prevValues,
                availableForSale: checked,
              }));
            }}
            errors={formPriceErrors}
          />
        </div>
      </TwDrawer>
    </div>
  );
}
